import { useEffect, useRef, type ReactNode } from "react";

import { Button } from "./button";

export interface PagerProps {
	/** Accessible name of the navigation: "Audit log pages", "Intake pages". */
	readonly label: string;
	/** The link to newer items, or null when there is no page that way — the pager then shows Previous disabled. */
	readonly previous: ReactNode | null;
	/** The link to older items, likewise. */
	readonly next: ReactNode | null;
}

/**
 * Previous and Next under a card. Reaching the first or the last page turns the link just followed into a disabled
 * button, which drops the keyboard focus; the focus then moves to the other link, so paging back is one key away.
 *
 * Router-agnostic, like everything in this folder: the caller passes the two links, and the pager itself supplies the
 * disabled Previous or Next button when a link is null.
 */
export function Pager({ label, previous, next }: PagerProps) {
	const navigationRef = useRef<HTMLElement>(null);
	const followedLinkRef = useRef(false);

	useEffect(() => {
		const element = navigationRef.current;
		if (element === null || !followedLinkRef.current) {
			return;
		}
		followedLinkRef.current = false;
		if (!element.contains(document.activeElement)) {
			element.querySelector("a")?.focus();
		}
	}, [previous, next]);

	return (
		<nav
			ref={navigationRef}
			aria-label={label}
			className="flex items-center justify-end gap-2 border-t border-line px-4 py-2.5"
			onClickCapture={event => {
				followedLinkRef.current = event.target instanceof Element && event.target.closest("a") !== null;
			}}
		>
			{previous ?? (
				<Button size="sm" disabled>
					Previous
				</Button>
			)}
			{next ?? (
				<Button size="sm" disabled>
					Next
				</Button>
			)}
		</nav>
	);
}
